import apiClient from '../apiClient';
import { Patient } from '../patientApi';
import type { Prescription } from '../prescriptionApi';
import type { ImagingOrder } from '../imagingOrderApi';
import type { Appointment } from '../appointmentApi';
import type { ClinicLabOrder } from './clinicLabOrdersApi';
// Aliased: this file already declares a local `Referral` (the SOAP-suggestion
// shape) further down, so the stored-referral entity type comes in renamed.
import type { Referral as StoredReferral } from './referralsApi';

/**
 * Everything a single encounter (note session) produced — prescriptions, lab
 * orders, imaging orders, referrals and follow-up appointments — gathered
 * across modules by the shared `medicalRecordId`. The read side of the "what
 * did this note lead to?" concept; each module still authors + browses its
 * own outputs, this just rolls them up against the visit.
 */
export interface EncounterOutcomes {
  medicalRecordId: string;
  patientId: string | null;
  prescriptions: Prescription[];
  labOrders: ClinicLabOrder[];
  imagingOrders: ImagingOrder[];
  referrals: StoredReferral[];
  appointments: Appointment[];
  counts: {
    prescriptions: number;
    labOrders: number;
    imagingOrders: number;
    referrals: number;
    appointments: number;
    total: number;
  };
}

/** The join the record actually carries for `provider` and `signedBy`. */
export interface RecordAuthor {
  id: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  email?: string;
}

export interface VitalSigns {
  id?: string;
  measuredAt: string;
  temperature?: number;
  temperatureUnit?: 'celsius' | 'fahrenheit';
  heartRate?: number;
  respiratoryRate?: number;
  bloodPressureSystolic?: number;
  bloodPressureDiastolic?: number;
  oxygenSaturation?: number;
  weight?: number;
  weightUnit?: 'kg' | 'lbs';
  height?: number;
  heightUnit?: 'cm' | 'inches';
  bmi?: number;
  notes?: string;
}

export interface Diagnosis {
  code: string;
  description: string;
  type: 'ICD-10' | 'ICD-9' | 'SNOMED';
}

export interface Procedure {
  code: string;
  description: string;
  type: 'CPT' | 'HCPCS';
}

/** A follow-up as reception sees it — no reason, just who and when. */
export interface FollowUpToBook {
  recordId: string;
  patientId: string;
  patientName: string;
  providerId: string | null;
  providerName: string | null;
  dueOn: string;
  /** "~2 wks" — computed from the dates, never parsed from the doctor's note. */
  interval: string;
  state: 'due' | 'upcoming';
  daysFromToday: number;
}

/** One follow-up the clinic owes — see getFollowUps. */
export interface FollowUp {
  recordId: string;
  patientId: string;
  patientName: string;
  providerId: string | null;
  /** YYYY-MM-DD — a calendar day, not an instant. */
  dueOn: string;
  note: string | null;
  state: 'due' | 'upcoming' | 'booked';
  /** Negative when overdue. */
  daysFromToday: number;
  bookedFor: string | null;
}

export interface MedicalRecord {
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  createdById: string | null;
  updatedById: string | null;

  patientId: string;
  providerId: string;
  appointmentId: string | null;

  visitDate: string;
  type: 'progress_note' | string;

  chiefComplaint: string | null;
  historyPresentIllness: string | null;
  pastMedicalHistory: string | null;
  medications: string | null;
  allergies: string | null;
  familyHistory: string | null;
  socialHistory: string | null;
  reviewOfSystems: string | null;
  physicalExam: string | null;

  subjective: string | null;
  objective: string | null;
  assessment: string | null;
  plan: string | null;

  diagnoses: Diagnosis[];
  procedures: string | null;

  status: 'draft' | 'signed' | 'amended' | 'deleted';
  signedAt: string | null;
  signedById: string | null;
  publishedToPatientAt: string | null;
  publishedToPatientById: string | null;
  lastModifiedById: string | null;

  notes: string | null;
  voiceTranscript: string | null;
  attachments: unknown[] | null;
  metadata: Record<string, unknown> | null;

  /**
   * UUID of the underlying recording_sessions row when this record was
   * generated from a voice consultation. Used to resolve the
   * audio_record (if retention was on) for inline feedback affordances.
   */
  recordingSessionId?: string | null;

  /** BW3.1 · Per-field AI provenance map. */
  fieldProvenance: Record<string, FieldProvenance> | null;

  /** BW3.3 · Overall AI confidence 0–1. */
  aiConfidence: number | null;

  /** Slice C: template this encounter is authored against; null = legacy SOAP. */
  templateId?: string | null;

  /**
   * Full templated form payload (every template.section.field id mapped
   * to its value). Standard SOAP fields are mirrored here; custom-section
   * values only persist here. Encrypted server-side.
   */
  templateData?: Record<string, unknown> | null;

  /**
   * AI-suggested structured actions persisted with the encounter:
   * labOrders, imagingOrders, appointments, referrals, prescriptions,
   * medicalHistoryItems. Set by the scribe pipeline on SOAP commit.
   * Independent of any template; templates can mirror values into
   * their own field ids when present.
   */
  suggestedActions?: Record<string, unknown> | null;

  patient: Patient;
  /**
   * The USER who authored the visit — `users`, joined on `providerId`. It was
   * typed `ProviderAvailability` (a weekly schedule window), which is a
   * different table with a different shape: the name lives at `.provider.
   * firstName` there and at `.firstName` here, so anything that tried to read
   * the author's name off a record either got `undefined` or did not compile.
   */
  provider: RecordAuthor;
  appointment: unknown | null;

  vitalSigns: VitalSigns[];
  signedBy: RecordAuthor | null;
}

export type ProvenanceState = 'ai' | 'ai_reviewed' | 'clinician';

export interface FieldProvenance {
  state: ProvenanceState;
  original?: string;
  updatedAt?: string;
  confidence?: number;
}


export interface CreateMedicalRecordDto {
  patientId: string;
  appointmentId?: string;
  visitDate: string;
  type:
  | 'progress_note'
  | 'consultation'
  | 'admission'
  | 'discharge'
  | 'procedure_note'
  | 'operative_note'
  | 'emergency'
  | 'follow_up';
  chiefComplaint?: string;
  historyPresentIllness?: string;
  pastMedicalHistory?: string;
  medications?: string;
  allergies?: string;
  familyHistory?: string;
  socialHistory?: string;
  reviewOfSystems?: string;
  physicalExam?: string;
  subjective?: string;
  objective?: string;
  assessment?: string;
  plan?: string;
  diagnoses?: Diagnosis[];
  procedures?: Procedure[];
  vitalSigns?: VitalSigns[];
  notes?: string;
  voiceTranscript?: string;
  /** Bind the new encounter to a template (e.g. AI document import). */
  templateId?: string | null;
  /** Custom template field values, keyed by template field id. */
  templateData?: Record<string, unknown> | null;
  /** Per-field AI provenance so the encounter shows "AI" review badges. */
  fieldProvenance?: Record<string, FieldProvenance>;
  /** Overall AI confidence (0–1) when the record was AI-seeded. */
  aiConfidence?: number;
}

export interface UpdateMedicalRecordDto {
  patientId?: string;
  appointmentId?: string;
  visitDate?: string;
  type?:
  | 'progress_note'
  | 'consultation'
  | 'admission'
  | 'discharge'
  | 'procedure_note'
  | 'operative_note'
  | 'emergency'
  | 'follow_up';
  chiefComplaint?: string;
  historyPresentIllness?: string;
  pastMedicalHistory?: string;
  medications?: string;
  allergies?: string;
  familyHistory?: string;
  socialHistory?: string;
  reviewOfSystems?: string;
  physicalExam?: string;
  subjective?: string;
  objective?: string;
  assessment?: string;
  plan?: string;
  diagnoses?: Diagnosis[];
  procedures?: Procedure[];
  vitalSigns?: VitalSigns[];
  status?: 'draft' | 'signed' | 'amended' | 'deleted';
  notes?: string;
  voiceTranscript?: string;
  fieldProvenance?: Record<string, FieldProvenance>;
  aiConfidence?: number;
  /** Slice C: links the encounter to its authoring template (or null to clear). */
  templateId?: string | null;
  /** Slice C: full templated form payload, keyed by template field id. */
  templateData?: Record<string, unknown> | null;
}

export interface FilterMedicalRecordDto {
  page?: number;
  limit?: number;
  patientId?: string;
  providerId?: string;
  type?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
}

export interface VoiceToSoapContext {
  age?: number;
  gender?: string;
  chiefComplaint?: string;
  medicalHistory?: string[];
  currentMedications?: string[];
  allergies?: string[];
  vitalSigns?: Record<string, unknown>;
}

export interface ProcessVoiceToSoapDto {
  audioData: string; // base64 encoded audio
  patientId?: string;
  medicalRecordId?: string;
  language?: string;
  soapSection?: 'subjective' | 'objective' | 'assessment' | 'plan' | 'full';
  context?: VoiceToSoapContext;
}

export interface MedicalEntities {
  symptoms?: string[];
  diagnoses?: string[];
  medications?: string[];
  procedures?: string[];
}

export interface SoapNote {
  subjective?: string;
  objective?: string;
  assessment?: string;
  plan?: string;
}

export interface LabOrder {
  testName: string;
  priority: 'stat' | 'urgent' | 'routine';
  notes?: string;
}

export interface ImagingOrderSuggestion {
  // Free-form modality from the AI (e.g. "X-ray", "CT chest", "Nuclear");
  // mapped to the entity enum on approve. See useAddRecordHandlers.
  modality: string;
  bodyPart: string;
  indication?: string;
  priority?: 'stat' | 'urgent' | 'routine';
  contrast?: boolean;
  notes?: string;
}

export interface AppointmentSuggestion {
  type: string;
  /** AI's relative hint ("2 weeks", "soon"). Used to seed the date picker. */
  timeframe: string;
  reason?: string;
  /** Concrete ISO date ("YYYY-MM-DD") once the clinician pins it in review. */
  date?: string;
  /** Concrete 24h time ("HH:MM") once the clinician pins it in review. */
  time?: string;
}

export interface Referral {
  specialty: string;
  reason: string;
  priority: 'stat' | 'urgent' | 'routine';
}

export interface PrescriptionSafetyFlag {
  /** 'unverified' = the drug name wasn't found in the transcript (possible
   * ASR mishearing); shown as a "verify" chip rather than dropping the Rx. */
  type: 'allergy' | 'duplicate' | 'unverified';
  severity?: string;
  message: string;
}

export interface PrescriptionSuggestion {
  medication: string;
  dosage: string;
  duration?: string;
  refills?: number;
  /** Deterministic backend Rx-safety flags (allergy conflict / duplicate
   * therapy) computed against the patient's structured chart. */
  safetyFlags?: PrescriptionSafetyFlag[];
}

// Medical History extraction from SOAP notes
export interface MedicalHistorySuggestion {
  type: 'chronic_disease' | 'allergy' | 'surgery' | 'family_history';
  name: string;
  details: {
    // For chronic diseases
    icdCode?: string;
    status?: 'active' | 'controlled' | 'resolved' | 'suspected';
    severity?: 'mild' | 'moderate' | 'severe';
    onsetDate?: string;

    // For allergies
    allergenType?: 'drug' | 'food' | 'environmental' | 'other';
    reaction?: string;

    // For surgeries
    date?: string;
    hospital?: string;
    outcome?: string;

    // For family history
    relation?: 'mother' | 'father' | 'sibling' | 'grandparent' | 'aunt_uncle' | 'other';
    ageAtOnset?: number;
    isDeceased?: boolean;

    // Common
    notes?: string;
  };
  confidence: number; // 0-1 extraction confidence
  sourceText?: string; // The text that triggered this extraction
}

export interface SuggestedActions {
  labOrders: LabOrder[];
  imagingOrders: ImagingOrderSuggestion[];
  appointments: AppointmentSuggestion[];
  referrals: Referral[];
  prescriptions: PrescriptionSuggestion[];
  medicalHistoryItems?: MedicalHistorySuggestion[]; // NEW: Auto-extracted medical history
}

export interface SoapRecommendation {
  field: 'subjective' | 'objective' | 'assessment' | 'plan' | 'medications' | 'general';
  text: string;
  priority: 'high' | 'medium' | 'low';
}

/**
 * Grounding-derived SOAP confidence from the scribe pipeline (cited mode):
 * the fraction of each section's cited claims whose quote verified against
 * the transcript. Present only when the backend ran in cited mode.
 */
export interface SoapConfidence {
  /** 0–1 overall grounded fraction across the four narrative sections. */
  overall: number;
  /** Per-section grounded fraction; a section with no claims is omitted. */
  fields: {
    subjective?: number;
    objective?: number;
    assessment?: number;
    plan?: number;
  };
  mode: 'cited';
}

export interface VoiceToSoapResponse {
  transcription: string;
  soapNote: SoapNote;
  /** Per-field + overall grounding confidence (cited mode only). */
  soapConfidence?: SoapConfidence;
  medicalEntities?: MedicalEntities;
  vitals?: {
    bloodPressure?: string;
    heartRate?: string;
    temperature?: string;
    respiratoryRate?: string;
    oxygenSaturation?: string;
    weight?: string;
    height?: string;
    bmi?: string;
  };
  medications?: string[];
  suggestedActions?: SuggestedActions;
  recommendations?: SoapRecommendation[];
  transcriptionConfidence?: number;
  processingTimeMs?: number;
  warnings?: string[];
  voiceTranscript?: string;
}





/**
 * What signing an encounter did to the drafts it produced.
 *
 * Present only when the clinic runs DEFERRED_ORDER_SIGNING — with the
 * flag off nothing is deferred, so there is nothing to report and the
 * field is absent. Items are named rather than counted: a doctor who
 * signs five scripts and gets "4 signed" cannot act on that, and the
 * one that failed is the one they need to know about.
 */
export interface SignedItemResult {
  kind: 'prescription' | 'referral';
  id: string;
  label: string;
  outcome: 'signed' | 'skipped' | 'failed';
  reason?: string;
}

export interface EncounterSignResult {
  attempted: boolean;
  items: SignedItemResult[];
  signed: number;
  failed: number;
}

export const medicalRecordsApi = {
  // Get all medical records with filters
  getMedicalRecords: async (filters: FilterMedicalRecordDto = {}) => {
    const params = new URLSearchParams();
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.patientId) params.append('patientId', filters.patientId);
    if (filters.providerId) params.append('providerId', filters.providerId);
    if (filters.type) params.append('type', filters.type);
    if (filters.status) params.append('status', filters.status);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.search) params.append('search', filters.search);

    const response = await apiClient.get(`/medical-records?${params.toString()}`);
    return response.data;
  },

  /**
   * Visits that asked to see the patient again and are not yet answered by
   * the schedule — `due` (date arrived) and `upcoming`. Booked ones only with
   * `includeBooked`. See MedicalRecordsService.followUps for how "booked" is
   * derived.
   */
  getFollowUps: async (params: { providerId?: string; includeBooked?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (params.providerId) q.set('providerId', params.providerId);
    if (params.includeBooked) q.set('includeBooked', 'true');
    const response = await apiClient.get(`/medical-records/follow-ups?${q.toString()}`);
    // Envelope: unwrap at the caller, as everywhere else in this client.
    return (response.data?.data ?? response.data) as FollowUp[];
  },

  /**
   * The front desk's view: who, how soon, with whom — no clinical text, and
   * gated on appointment:read, which reception holds (it does not hold
   * medical_records:read, correctly).
   */
  getFollowUpsToBook: async () => {
    const response = await apiClient.get('/medical-records/follow-ups/to-book');
    return (response.data?.data ?? response.data) as FollowUpToBook[];
  },

  // Get single medical record by ID
  getMedicalRecordById: async (id: string) => {
    const response = await apiClient.get(`/medical-records/${id}`);
    return response.data as {
      data: MedicalRecord;
    }
  },

  // Everything this encounter led to — Rx / labs / imaging / referrals /
  // follow-up appointments — scoped to the one note via medicalRecordId.
  // Powers the "What this visit produced" rollup on the encounter view.
  getEncounterOutcomes: async (id: string): Promise<EncounterOutcomes> => {
    const response = await apiClient.get(`/medical-records/${id}/outcomes`);
    // Unwrap the `{ success, data }` envelope (EMR unwraps at the caller).
    return response.data.data as EncounterOutcomes;
  },

  // Get medical records for a specific appointment
  getMedicalRecordsByAppointment: async (appointmentId: string): Promise<MedicalRecord[]> => {
    const response = await apiClient.get(`/medical-records/by-appointment/${appointmentId}`);
    return response.data;
  },

  // Create new medical record
  createMedicalRecord: async (data: CreateMedicalRecordDto) => {
    const response = await apiClient.post('/medical-records', data);
    return response.data;
  },

  // Update medical record
  updateMedicalRecord: async (id: string, data: UpdateMedicalRecordDto) => {
    const response = await apiClient.patch(`/medical-records/${id}`, data);
    return response.data;
  },

  // Sign medical record — PIN is the clinician's 4-digit attestation
  // factor (bcrypt-hashed server-side, lockout after 5 wrong tries).
  signMedicalRecord: async (
    id: string,
    pin: string,
  ): Promise<MedicalRecord & { orderSigning?: EncounterSignResult }> => {
    const response = await apiClient.post(`/medical-records/${id}/sign`, {
      pin,
    });
    // Unwrap the `{ success, data, ... }` envelope so callers receive the
    // signed MedicalRecord itself — `onSigned`/`handleSigned` read
    // `signed.patientId`/`signed.id` to refetch + navigate, which were
    // `undefined` when handed the raw envelope (post-sign nav fell back
    // to the bare `/patients` list instead of the signed record).
    return response.data.data;
  },

  /**
   * Materialize the AI-extracted `suggested_actions` blob on the
   * record into real Rx / lab / imaging order rows linked back to the
   * encounter. Best-effort: returns per-bucket {ok, failed} counts so
   * the caller can toast a meaningful summary. Rx proposals flagged by
   * the interaction checker come back in `prescriptionsHeld` instead
   * of being auto-created (they'd auto-SIGN otherwise) — the doctor
   * prescribes those deliberately through the Rx form, which shows
   * the same warning.
   */
  commitSuggestedActions: async (
    id: string,
    /**
     * Which suggestions to create, by index into each bucket. Omit to
     * create everything, which is what a caller with no picker does.
     * Indices only mean anything against the blob the caller was shown,
     * so read the record immediately before posting a selection.
     */
    selection?: Partial<Record<string, number[]>>,
  ): Promise<{
    prescriptions: { ok: number; failed: number; held?: number };
    imagingOrders: { ok: number; failed: number };
    labOrders: { ok: number; failed: number };
    referrals: { ok: number; failed: number };
    appointments: { ok: number; failed: number };
    prescriptionsHeld?: Array<{ medication: string; reasons: string[] }>;
  }> => {
    const response = await apiClient.post(
      `/medical-records/${id}/commit-suggested-actions`,
      selection ? { selection } : {},
    );
    /* UNWRAP THE ENVELOPE. The backend answers `{ success, data }` like every
       other route; this returned the envelope raw while declaring the inner
       shape, so both callers read `undefined` where they expected counts.
       AttestationModal's post-sign summary did `summary.prescriptions.ok` and
       threw into its own catch — so a doctor signing a note with proposals was
       never told what had been created — and the encounter's workup pane
       reported "Nothing was created" about orders it had just made. */
    return response.data.data ?? response.data;
  },

  // Delete medical record
  deleteMedicalRecord: async (id: string) => {
    const response = await apiClient.delete(`/medical-records/${id}`);
    return response.data;
  },

  // Make a signed encounter visible in the patient portal. Backend
  // enforces signed-status; the call 400s on draft records.
  publishToPatient: async (id: string): Promise<MedicalRecord> => {
    const response = await apiClient.post<MedicalRecord>(
      `/medical-records/${id}/publish-to-patient`,
    );
    return response.data;
  },

  unpublishFromPatient: async (id: string): Promise<MedicalRecord> => {
    const response = await apiClient.delete<MedicalRecord>(
      `/medical-records/${id}/publish-to-patient`,
    );
    return response.data;
  },

  // Voice-to-SOAP: Process voice and generate SOAP note
  processVoiceToSoap: async (data: ProcessVoiceToSoapDto): Promise<VoiceToSoapResponse> => {
    const response = await apiClient.post('/medical-records/voice-to-soap', data);
    return response.data;
  },

  // Voice-to-SOAP: Create medical record from voice
  createMedicalRecordFromVoice: async (data: ProcessVoiceToSoapDto): Promise<MedicalRecord> => {
    const response = await apiClient.post('/medical-records/voice-to-soap/create-record', data);
    return response.data;
  },
};
